import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SubscriptionManagement = ({ user, login, isLoggedIn }) => {
  const [customerId, setCustomerId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showUpgradeCancel, setShowUpgradeCancel] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [accountType, setAccountType] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/");
      return;
    }

    const fetchUsageInfo = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user-usage-info`
        );
        console.log("Usage Info:", res.data);
        const sub = res.data?.subscription;
        const currentAccountType = sub?.account_type?.toLowerCase();
        setAccountType(currentAccountType);

        if (currentAccountType === "paid" && !sub?.cancellation_requested) {
          setShowUpgradeCancel(true);
        } else if (currentAccountType === "paid" && sub?.cancellation_requested) {
          setShowRenew(true);
        }

        setCustomerId(sub?.customerId || "");
      } catch (err) {
        console.error("Failed to fetch user usage info", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsageInfo();
  }, [isLoggedIn, navigate]);

  const handleManageSubscription = async () => {


    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/create-billing-portal-session`,
        { customerId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error("Error redirecting to billing portal:", error);
      alert("Unable to redirect to billing portal.");
    }
  };

  const submitFeedbackAndRedirect = async () => {
    if (!reason || !comments.trim()) {
      setErrorMessage(
        "Please select a reason and provide your valuable comments."
      );
      return;
    }

    setErrorMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/payment/cancelSubscription/`,
        { reason, comments }
      );
    } catch (err) {
      console.error("Failed to submit feedback:", err);
    }

    handleManageSubscription();
  };

  const handleOpenFeedback = () => setOpenFeedback(true);
  const handleCloseFeedback = () => setOpenFeedback(false);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      className="subscription-management"
      style={{
        paddingTop: "4rem",
        paddingBottom: "25rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
        textAlign: "center",
      }}
    >
      <h2>Manage Your Subscription</h2>

      {accountType === "free" && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Box width="50%" mx="auto">
            <TextField
              value="You are currently on the free plan. Upgrade to access subscription management features."
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
          </Box>
        </Box>
      )}

      {showUpgradeCancel && (
        <>
          <p>
            Click below to update your billing details or cancel your
            subscription.
          </p>
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              color="success"
              onClick={handleManageSubscription}
            >
              Upgrade Plan
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleOpenFeedback}
            >
              Cancel Subscription
            </Button>
          </Box>
        </>
      )}

      {showRenew && (
        <>
          <p>Click below to renew your subscription</p>
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              color="success"
              onClick={handleManageSubscription}
            >
              Renew Plan
            </Button>
          </Box>
        </>
      )}

      <Dialog
        open={openFeedback}
        onClose={handleCloseFeedback}
        PaperProps={{
          sx: { width: "500px", maxWidth: "90%", height: "auto" },
        }}
      >
        <DialogTitle>Help us improve</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Reason for cancellation"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            margin="normal"
            required
          >
            <MenuItem value="">Select a reason</MenuItem>
            <MenuItem value="Too expensive">Too expensive</MenuItem>
            <MenuItem value="Missing features">Missing features</MenuItem>
            <MenuItem value="Found Alternative">Found Alternative</MenuItem>
            <MenuItem value="Not using enough">Not using enough</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>

          <TextField
            label="Additional comments"
            multiline
            rows={4}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            margin="normal"
            required
          />

          {errorMessage && (
            <Box mt={1} color="error.main" fontSize="0.9rem">
              {errorMessage}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseFeedback} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={submitFeedbackAndRedirect}
            variant="contained"
            color="primary"
          >
            Continue to Billing
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SubscriptionManagement;